/**
 * Admin Service Inquiries inbox.
 *
 * Lists pending Additional-Services inquiries with a one-click flow:
 *   Inquiry → "Send pay link" → Stripe Checkout Session created →
 *   email auto-sent to customer with secure link → row flips to "quoted".
 *
 * Custom-price override lives in the same modal — useful for VA Placement
 * bundles or DFY multi-market quotes.
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Mail, CreditCard, Phone, Edit3, Check, Loader2 } from "lucide-react";

const STATUS_COLORS = {
  new:    "bg-emerald-100 text-emerald-800 border-emerald-200",
  quoted: "bg-amber-100  text-amber-800 border-amber-200",
  paid:   "bg-blue-100   text-blue-800 border-blue-200",
};

export default function AdminServiceInquiries() {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [picked, setPicked]   = useState(null);  // inquiry being quoted

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/services/inquiries");
      setRows(r.data.inquiries || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load inquiries");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const groups = useMemo(() => {
    const g = { new: [], quoted: [], paid: [], other: [] };
    for (const r of rows) {
      const k = r.status in g ? r.status : "other";
      g[k].push(r);
    }
    return g;
  }, [rows]);

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="admin-service-inquiries">
      <div className="mb-6">
        <h1 className="font-display font-extrabold text-3xl">Additional-Services Inquiries</h1>
        <p className="text-sm text-[var(--ink-2)] mt-1">
          Send a secure Stripe payment link in one click. Custom pricing for bundles is built in.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-[var(--ink-3)]"><Loader2 size={16} className="animate-spin" /> Loading…</div>
      ) : rows.length === 0 ? (
        <EmptyState />
      ) : (
        <>
          <Group title="New — needs follow-up"    rows={groups.new}    onQuote={setPicked} />
          <Group title="Quoted (awaiting payment)" rows={groups.quoted} onQuote={setPicked} />
          <Group title="Paid"                      rows={groups.paid}   onQuote={null} />
        </>
      )}

      {picked && (
        <QuoteModal
          inquiry={picked}
          onClose={() => setPicked(null)}
          onSent={() => { setPicked(null); load(); }}
        />
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="rounded-xl border border-dashed border-[var(--line)] p-8 text-center">
      <Mail className="mx-auto mb-2 text-[var(--ink-3)]" size={26} />
      <div className="font-semibold text-[var(--ink)]">No inquiries yet</div>
      <div className="text-sm text-[var(--ink-3)] mt-1">
        When someone clicks <em>Talk to us</em> on an Additional Service, it shows up here.
      </div>
    </div>
  );
}

function Group({ title, rows, onQuote }) {
  if (!rows || rows.length === 0) return null;
  return (
    <section className="mb-8" data-testid={`admin-services-group-${title.split(" ")[0].toLowerCase()}`}>
      <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--ink-3)] mb-2">{title} · {rows.length}</div>
      <div className="space-y-2">
        {rows.map((r) => <Row key={r.id} r={r} onQuote={onQuote} />)}
      </div>
    </section>
  );
}

function Row({ r, onQuote }) {
  const badge = STATUS_COLORS[r.status] || "bg-slate-100 text-slate-700 border-slate-200";
  return (
    <div className="border border-[var(--line)] rounded-lg p-4 bg-white" data-testid={`admin-service-inquiry-${r.id}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <div className="flex items-center gap-2 mb-0.5">
            <span className="font-bold text-[var(--ink)]">{r.service_name}</span>
            <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${badge}`}>{r.status}</span>
          </div>
          <div className="text-sm text-[var(--ink-2)]">
            <span className="font-semibold">{r.name}</span> · {r.email}
            {r.phone && <span className="ml-1"><Phone size={11} className="inline mr-0.5" />{r.phone}</span>}
          </div>
        </div>
        {onQuote && (
          <button
            type="button"
            onClick={() => onQuote(r)}
            className="btn-money text-xs"
            data-testid={`admin-service-inquiry-${r.id}-quote`}
          >
            <CreditCard size={12} /> Send pay link
          </button>
        )}
      </div>
      {r.notes && (
        <div className="text-xs text-[var(--ink-2)] mt-2 bg-[var(--bg)] rounded p-2 whitespace-pre-wrap">
          {r.notes}
        </div>
      )}
      <div className="text-[10px] text-[var(--ink-3)] mt-2">
        Captured {new Date(r.captured_at).toLocaleString()}
      </div>
    </div>
  );
}

function QuoteModal({ inquiry, onClose, onSent }) {
  const [customPrice, setCustomPrice] = useState(false);
  const [amount, setAmount] = useState((inquiry.service_default_price_cents || 49900) / 100);
  const [note, setNote]     = useState("");
  const [sending, setSending] = useState(false);

  const submit = async () => {
    setSending(true);
    try {
      const payload = {
        inquiry_id: inquiry.id,
        amount_cents: customPrice ? Math.round(amount * 100) : null,
        note: note.trim(),
      };
      const r = await api.post("/admin/services/quote", payload);
      toast.success("Payment link sent ✉️");
      // Copy the pay URL to clipboard for convenience.
      if (r.data?.pay_url && navigator.clipboard) {
        navigator.clipboard.writeText(r.data.pay_url).catch(() => {});
      }
      onSent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send quote");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-zinc-900/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl border border-[var(--line)] w-full max-w-md p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="font-display font-bold text-lg">Send pay link</div>
        <div className="text-sm text-[var(--ink-2)] mt-1">
          {inquiry.service_name} → <strong>{inquiry.email}</strong>
        </div>

        <div className="mt-4">
          <label className="flex items-center gap-2 text-sm mb-2 cursor-pointer">
            <input
              type="checkbox"
              checked={customPrice}
              onChange={(e) => setCustomPrice(e.target.checked)}
              data-testid="admin-quote-custom-price-toggle"
            />
            <Edit3 size={13} /> Custom price (override default {currency(inquiry.service_default_price_cents)})
          </label>
          {customPrice && (
            <div className="flex items-center gap-2 mb-3">
              <span className="text-sm text-[var(--ink-2)]">$</span>
              <input
                type="number"
                min="1"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(parseFloat(e.target.value) || 0)}
                className="w-32 px-3 py-2 border border-[var(--line)] rounded-lg text-sm"
                data-testid="admin-quote-amount"
              />
              <span className="text-xs text-[var(--ink-3)]">
                ({inquiry.service_mode === "subscription" ? "/mo" : "one-time"})
              </span>
            </div>
          )}
          <textarea
            placeholder="Optional note added to the email (e.g. 'As discussed on our call…')"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            className="w-full px-3 py-2 border border-[var(--line)] rounded-lg text-sm resize-none mt-1"
            data-testid="admin-quote-note"
          />
        </div>

        <div className="flex gap-2 mt-5">
          <button type="button" onClick={onClose} className="btn-outline flex-1" data-testid="admin-quote-cancel">
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={sending}
            className="btn-money flex-1 justify-center disabled:opacity-60"
            data-testid="admin-quote-submit"
          >
            {sending ? <Loader2 size={14} className="animate-spin" /> : <><Check size={14} /> Send link</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function currency(cents) {
  if (cents == null) return "$—";
  return `$${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 0 })}`;
}
